import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable, { TableStyles } from 'react-data-table-component';
import moment from 'moment';
import * as sessionNotesService from '../../services/sessionNotes';
import * as franchiseService from '../../services/franchise';
import * as centreService from '../../services/centre';
import { base64Encode } from '../../helpers/base64url';

const ListSessionNotes: React.FC = () => {
  const [sessionNotes, setSessionNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const [franchises, setFranchises] = useState<any[]>([]);
  const [centres, setCentres] = useState<any[]>([]);
  const [reviewEnabled, setReviewEnabled] = useState(false);
  const [dateError, setDateError] = useState('');
  const [filters, setFilters] = useState({
    franchiseId: '',
    centreId: '',
    fromDate: '',
    toDate: ''
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const initialFetchTriggered = useRef(false);

  const canAccess = useSelector((state: any) => state.auth.permissions?.['session-notes']);

  const customStyle: TableStyles = {
    head: {
      style: {
        color: "#5E5873",
        fontSize: "14px",
        fontWeight: 900,
      },
    },
    headCells: {
      style: {
        borderColor: "#E9ECEF",
        textTransform: "uppercase",
        letterSpacing: "1px",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#F7F7F8",
        minHeight: "44px",
        fontWeight: 500,
      },
    },
    rows: {
      style: {
        fontSize: "14px",
      },
    },
  };

  useEffect(() => {
    fetchFranchises();
    // Set loading true during initial load
    setLoading(true);
  }, []);

  useEffect(() => {
    // Auto-select franchise if only one is available
    if (franchises.length === 1 && !filters.franchiseId) {
      const selectedFranchise = franchises[0];
      setFilters(prev => ({
        ...prev,
        franchiseId: selectedFranchise.id.toString()
      }));
      setReviewEnabled(selectedFranchise?.enabled_review_session_notes || false);
    } else if (franchises.length > 1) {
      // If multiple franchises and no specific franchise selected, check if any has review enabled
      if (!filters.franchiseId) {
        const anyFranchiseHasReview = franchises.some(f => f.enabled_review_session_notes === true);
        setReviewEnabled(anyFranchiseHasReview);
      }
      // If multiple franchises, mark initial load as complete
      setIsInitialLoad(false);
      setLoading(false);
    } else if (franchises.length === 0) {
      // If no franchises, mark initial load as complete
      setIsInitialLoad(false);
      setLoading(false);
    }
  }, [franchises, filters.franchiseId]);

  useEffect(() => {
    // Auto-select centre if only one is available
    if (centres.length === 1 && !filters.centreId && filters.franchiseId) {
      // Batch both state updates together
      setFilters(prev => ({
        ...prev,
        centreId: centres[0].id.toString()
      }));
      setIsInitialLoad(false);
    } else if (centres.length > 1 || (centres.length === 0 && filters.franchiseId)) {
      // If multiple centres or no centres, mark initial load as complete
      setIsInitialLoad(false);
    }
  }, [centres, filters.centreId, filters.franchiseId]);

  useEffect(() => {
    // Only fetch once when initial load completes
    if (canAccess?.list && !isInitialLoad && !initialFetchTriggered.current) {
      initialFetchTriggered.current = true;
      fetchSessionNotes(currentPage, perPage, sortField, sortDirection);
    }
  }, [canAccess, isInitialLoad]);

  // Separate effect for manual filter changes
  useEffect(() => {
    // Only respond to filter changes after initial fetch has been triggered
    if (canAccess?.list && initialFetchTriggered.current) {
      const timeoutId = setTimeout(() => {
        fetchSessionNotes(currentPage, perPage, sortField, sortDirection);
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [filters.franchiseId, filters.centreId, filters.fromDate, filters.toDate]);

  useEffect(() => {
    if (filters.franchiseId) {
      // Clear centreId immediately when franchise changes to prevent sending old centre with new franchise
      setFilters(prev => ({ ...prev, centreId: '' }));
      fetchCentres(parseInt(filters.franchiseId));
    } else {
      setCentres([]);
      setFilters(prev => ({ ...prev, centreId: '' }));
    }
  }, [filters.franchiseId]);

  const fetchFranchises = async () => {
    try {
      const response = await franchiseService.getAllActiveFranchise();
      if (response?.success) {
        setFranchises(response.collections || []);
      }
    } catch (error) {
      toast.error('Failed to fetch franchises');
    }
  };

  const fetchCentres = async (franchiseId: number) => {
    try {
      const response = await centreService.getCentresByFranchiseId(franchiseId);
      if (response?.success) {
        setCentres(response.collections || []);
      }
    } catch (error) {
      toast.error('Failed to fetch centres');
    }
  };

  const fetchSessionNotes = async (page: number, perPage: number, sortField: string, sortDirection: string) => {
    setLoading(true);
    try {
      const response = await sessionNotesService.getSessionNotes({
        page,
        perPage,
        sortField,
        sortDirection,
        ...filters
      });
      if (response?.success) {
        setSessionNotes(response.collections?.data || []);
        setTotalRows(response.collections?.pagination?.total || 0);
      } else {
        toast.error(response?.message || 'Failed to fetch session notes');
      }
    } catch (error) {
      toast.error('Failed to fetch session notes');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    let newFilters = { ...filters, [field]: value };
    
    // Clear centre when franchise changes
    if (field === 'franchiseId') {
      newFilters.centreId = '';
    }
    
    setFilters(newFilters);
    setCurrentPage(1);
    
    // Validate date range
    if (field === 'fromDate' || field === 'toDate') {
      const fromDate = field === 'fromDate' ? value : filters.fromDate;
      const toDate = field === 'toDate' ? value : filters.toDate;
      
      if (fromDate && toDate && new Date(toDate) < new Date(fromDate)) {
        setDateError('To date cannot be earlier than From date');
      } else {
        setDateError('');
      }
    }
    
    // Ensure initial load flag is false when user manually changes filters
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
    
    // Update review enabled status when franchise changes
    if (field === 'franchiseId') {
      if (value) {
        // Specific franchise selected
        const selectedFranchise = franchises.find(f => f.id.toString() === value);
        setReviewEnabled(selectedFranchise?.enabled_review_session_notes || false);
      } else {
        // "All Franchises" selected - check if any franchise has review enabled
        const anyFranchiseHasReview = franchises.some(f => f.enabled_review_session_notes === true);
        setReviewEnabled(anyFranchiseHasReview);
      }
    }
  };

  const handleReset = () => {
    setFilters({ franchiseId: '', centreId: '', fromDate: '', toDate: '' });
    setDateError('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchSessionNotes(page, perPage, sortField, sortDirection);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    fetchSessionNotes(page, newPerPage, sortField, sortDirection);
  };

  const handleSort = async (column: any, sortDirection: string) => {
    setSortField(column.sortField);
    setSortDirection(sortDirection);
    fetchSessionNotes(currentPage, perPage, column.sortField, sortDirection);
  };

  const columns = [
    {
      name: 'Session Date',
      selector: (row: any) => row.session_date,
      cell: (row: any) => moment(row.session_date).format('DD/MM/YYYY'),
      sortable: true,
      sortField: 'session_date',
      minWidth: '120px',
    },
    {
      name: 'Franchise Name',
      selector: (row: any) => row.franchise_name,
      sortable: true,
      sortField: 'franchise_name',
      wrap: true,
      minWidth: '150px',
    },
    {
      name: 'Centre Name',
      selector: (row: any) => row.centre_name || 'N/A',
      sortable: true,
      sortField: 'centre_name',
      wrap: true,
      minWidth: '150px',
    },
    {
      name: 'Status',
      cell: (row: any) => (
        <div>
          {row.total_draft_count > 0 && (
            <span className="badge badge-secondary mr-1" title="Draft">
              Draft: {row.total_draft_count}
            </span>
          )}
          {row.total_review_pending_count > 0 && (
            <span className="badge badge-warning mr-1" title="Review Pending">
              Review: {row.total_review_pending_count}
            </span>
          )}
          {row.total_published_count > 0 && (
            <span className="badge badge-success" title="Published">
              Published: {row.total_published_count}
            </span>
          )}
        </div>
      ),
      minWidth: '250px',
    },
    {
      name: 'Actions',
      cell: (row: any) => {
        const sessionDate = moment(row.session_date).format('YYYY-MM-DD');
        const data = {
          date: sessionDate,
          franchise_id: row.franchise_id,
          centre_id: row.centre_id || null
        };
        const encodedData = base64Encode(JSON.stringify(data));
        
        return (
          <div>
            {canAccess?.view && (
              <Link
                to={`/session-notes/view/${encodedData}`}
                className="btn btn-info btn-xs mr-1"
                title="View"
              >
                <i className="fas fa-eye"></i>
              </Link>
            )}
            {canAccess?.edit && row.total_draft_count > 0 && (
              <Link
                to={`/session-notes/edit/${encodedData}`}
                className="btn btn-primary btn-xs mr-1"
                title="Edit"
              >
                <i className="fas fa-edit"></i>
              </Link>
            )}
            {canAccess?.review && parseInt(row.total_review_pending_count) > 0 && (
              <Link
                to={`/session-notes/review/${encodedData}`}
                className="btn btn-warning btn-xs"
                title="Review"
              >
                <i className="fas fa-clipboard-check"></i>
              </Link>
            )}
          </div>
        );
      },
      minWidth: '150px',
    },
  ];

  if (!canAccess?.list) {
    return (
      <div className="">
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>Session Notes</h1>
              </div>
            </div>
          </div>
        </section>
        <section className="content">
          <div className="container-fluid">
            <div className="alert alert-warning">
              You do not have permission to access session notes.
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Session Notes</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item active">Session Notes</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="card-title">All Session Notes</h3>
                <div>
                  {canAccess?.review && reviewEnabled && (
                    <Link to="/session-notes/review" className="btn btn-warning btn-sm mr-2">
                      <i className="fas fa-clipboard-check mr-2"></i>
                      Review Session Notes
                    </Link>
                  )}
                  {canAccess?.create && (
                    <>
                      {/* <Link to="/session-notes/add" className="btn btn-primary btn-sm mr-2">
                        <i className="fas fa-plus mr-2"></i>
                        Add by Subject
                      </Link> */}
                      <Link to="/session-notes/add-by-student" className="btn btn-success btn-sm">
                        <i className="fas fa-user-plus mr-2"></i>
                        Add Session Notes
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="card-body">
              {/* Info text for selected franchise and centre */}
              {franchises.length === 1 && centres.length === 1 && filters.franchiseId && filters.centreId && (
                <div className="mb-3 p-2" style={{ fontSize: '14px', color: '#17a2b8', backgroundColor: '#e7f6f8', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>Franchise:</strong> {franchises.find(f => f.id.toString() === filters.franchiseId)?.name}
                  <span className="mx-2">•</span>
                  <strong>Centre:</strong> {centres.find(c => c.id.toString() === filters.centreId)?.name}
                </div>
              )}
              {franchises.length === 1 && centres.length > 1 && filters.franchiseId && (
                <div className="mb-3 p-2" style={{ fontSize: '14px', color: '#17a2b8', backgroundColor: '#e7f6f8', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>Franchise:</strong> {franchises.find(f => f.id.toString() === filters.franchiseId)?.name}
                </div>
              )}
              {franchises.length > 1 && centres.length === 1 && filters.franchiseId && filters.centreId && (
                <div className="mb-3 p-2" style={{ fontSize: '14px', color: '#17a2b8', backgroundColor: '#e7f6f8', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>Centre:</strong> {centres.find(c => c.id.toString() === filters.centreId)?.name}
                </div>
              )}

              {/* Filters */}
              <div className="row mb-3">
                {franchises.length > 1 && (
                  <div className="col-md-3">
                    <label>Franchise</label>
                    <select
                      className="form-control form-control-sm"
                      value={filters.franchiseId}
                      onChange={(e) => handleFilterChange('franchiseId', e.target.value)}
                    >
                      <option value="">All Franchises</option>
                      {franchises.map(franchise => (
                        <option key={franchise.id} value={franchise.id}>
                          {franchise.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {centres.length > 1 && (
                  <div className="col-md-3">
                    <label>Centre</label>
                    <select
                      className="form-control form-control-sm"
                      value={filters.centreId}
                      onChange={(e) => handleFilterChange('centreId', e.target.value)}
                      disabled={!filters.franchiseId}
                    >
                      <option value="">All Centres</option>
                      {centres.map(centre => (
                        <option key={centre.id} value={centre.id}>
                          {centre.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className={
                  franchises.length === 1 && centres.length === 1 ? "col-md-4" :
                  franchises.length > 1 && centres.length > 1 ? "col-md-2" :
                  "col-md-3"
                }>
                  <label>From Date</label>
                  <input
                    type="date"
                    className={`form-control form-control-sm ${dateError ? 'is-invalid' : ''}`}
                    value={filters.fromDate}
                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  />
                </div>
                <div className={
                  franchises.length === 1 && centres.length === 1 ? "col-md-4" :
                  franchises.length > 1 && centres.length > 1 ? "col-md-2" :
                  "col-md-3"
                }>
                  <label>To Date</label>
                  <input
                    type="date"
                    className={`form-control form-control-sm ${dateError ? 'is-invalid' : ''}`}
                    value={filters.toDate}
                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  />
                  {dateError && (
                    <div className="invalid-feedback d-block" style={{ fontSize: '14px' }}>
                      {dateError}
                    </div>
                  )}
                </div>
                <div className={
                  franchises.length === 1 && centres.length === 1 ? "col-md-4" :
                  franchises.length > 1 && centres.length > 1 ? "col-md-2" :
                  "col-md-3"
                }>
                  <label>&nbsp;</label>
                  <div>
                    <button className="btn btn-secondary btn-sm btn-block" onClick={handleReset}>
                      <i className="fas fa-redo mr-1"></i>
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              {/* DataTable */}
              <DataTable
                columns={columns}
                data={sessionNotes}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                onSort={handleSort}
                sortServer
                customStyles={customStyle}
                highlightOnHover
                pointerOnHover
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ListSessionNotes;
